import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import styled from "styled-components";
import { useSelector } from "react-redux";

import { Block, Loading } from "commons";
import {
    getClientDisplayName,
    getLocationDisplayName
} from "commons/src/helpers/FormattingHelpers";
import { useVisibleClients, useLocationSelection } from "commons/src/helpers/adviserViewHelpers";

import { Combobox } from "./Combobox";
import { AdviserViewMessage } from "./AdviserViewMessage";

export function SelectGroup({ onClientSelect, visibleLocations = [], handleClientsQueryError }) {
    const { previousLocationId } = useSelector(state => ({
        previousLocationId: state.user?.selection?.locationId
    }));

    const { selectedLocation, setSelectedLocation } = useLocationSelection(
        visibleLocations,
        previousLocationId
    );

    const { clientsLoading, visibleClients } = useVisibleClients(
        selectedLocation,
        handleClientsQueryError
    );

    return (
        <StyledBlock>
            <Typography variant="h1" color="primary">
                Nucleus Go adviser login
            </Typography>
            <Content>
                <GroupContainer>
                    {visibleLocations.length > 0 ? (
                        <Combobox
                            selectedItem={selectedLocation}
                            allItems={visibleLocations}
                            label="Select a firm"
                            itemToString={getLocationDisplayName}
                            onSelect={item => setSelectedLocation(item)}
                            type="firm"
                        />
                    ) : (
                        <Typography variant="h3">There are no locations available</Typography>
                    )}
                    {selectedLocation && visibleClients.length > 0 ? (
                        <Combobox
                            allItems={visibleClients}
                            label="Select or search for the client"
                            itemToString={getClientDisplayName}
                            onSelect={onClientSelect}
                            type="client"
                        />
                    ) : null}
                    {selectedLocation && visibleClients.length === 0 && !clientsLoading ? (
                        <Typography variant="h3">
                            You have no clients available in selected location
                        </Typography>
                    ) : null}
                    {clientsLoading ? (
                        <StyledLoading size={24} data-testid="clients-loading-indicator" />
                    ) : null}
                </GroupContainer>
                <AdviserViewMessage />
            </Content>
        </StyledBlock>
    );
}

const GroupContainer = styled.section`
    height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 2rem;
`;

const Content = styled.section`
    width: 100%;
    max-width: 600px;
    margin: 2rem auto 0 auto;
`;

const StyledBlock = styled(Block)`
    width: 100%;
    max-width: 800px;
    padding: 2rem;
`;

const StyledLoading = styled(Loading)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

SelectGroup.propTypes = {
    onClientSelect: PropTypes.func,
    visibleLocations: PropTypes.array,
    handleClientsQueryError: PropTypes.func
};
